import { ref, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { MenuEntry } from '@/types/navigation'
import { useNavigation } from './useNavigation'

export const useSideNav = () => {
  const { items, selectedItemId: activeRoute } = useNavigation()
  const router = useRouter()
  const openGroups = ref(new Set<string>())
  const subLevelRefMap = new Map<string, Element>()

  const isGroupOpen = (label: string) => openGroups.value.has(label)

  const isActive = (entry: MenuEntry) => entry.routeName === activeRoute.value

  const isEntrySelected = (entry: MenuEntry) =>
    entry.children?.length
      ? entry.children.some((child) => child.routeName === activeRoute.value)
      : isActive(entry)

  watch(
    activeRoute,
    (routeName) => {
      if (!routeName) return
      items.value
        .filter((entry) => entry.children?.some((child) => child.routeName === routeName))
        .forEach((entry) => openGroups.value.add(entry.i18nLabel))
    },
    { immediate: true }
  )

  const onDetailToggle = (event: Event, label: string) => {
    const isNowOpen = (event.target as HTMLDetailsElement).open
    if (isNowOpen) {
      openGroups.value.add(label)
      nextTick(() =>
        subLevelRefMap.get(label)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      )
    } else {
      openGroups.value.delete(label)
    }
  }

  const setSubLevelRef = (el: Element | null, label: string) => {
    if (el) subLevelRefMap.set(label, el)
    else subLevelRefMap.delete(label)
  }

  const getEntryHref = (entry: MenuEntry) => {
    if (entry.url) return entry.url
    if (entry.routeName) {
      try {
        return router.resolve({ name: entry.routeName }).href
      } catch {
        return '#'
      }
    }
    return '#'
  }

  return {
    openGroups,
    isGroupOpen,
    isActive,
    isEntrySelected,
    onDetailToggle,
    setSubLevelRef,
    getEntryHref
  }
}
